import { ALL_BOARDS, BOARDS, BOARDS_BY_ID, GAUNTLET_BOARDS } from './boards.ts';
import { BUILD_STAMP, GAME_NAME, GAME_VERSION, HINTS, SHAFT_LABEL, TESTER_URL } from './config.ts';
import type { ArtLoadReport } from './render/pixi/textures.ts';
import { AudioCues, Haptics } from './audio/audio.ts';
import { InputController } from './input/input.ts';
import { computeLayout, type Insets, type Layout } from './render/layout.ts';
import { PixiRenderer, type HudInfo } from './render/pixi/scene.ts';
import { PLAYER_SPEEDS, loadSettings, saveSettings, type Settings } from './settings.ts';
import {
  DEFAULTS,
  GAUNTLET_TICKS,
  NO_INPUT,
  Recorder,
  configFromRecording,
  createSim,
  hashState,
  moverPos,
  replay,
  step,
  unpackInput,
  type InputFrame,
  type Recording,
  type SimConfig,
  type SimEvent,
  type SimState,
} from './sim/index.ts';
import { Telemetry, clearStoredSessions, loadStoredSessions } from './telemetry/telemetry.ts';
import { exportSaveJson, importSave } from './save.ts';
import { LevelCardScreen, ShaftScreen, TitleScreen, type Pick, type ShaftLevel } from './render/pixi/screens.ts';
import { GAUNTLET_BEST_KEY, consumeKit, kitStock, levelProgress, loadProgress, markSeen, nextLevelIndex, recordAttempt, recordClear, type Progress } from './progress.ts';

type Screen = 'title' | 'shaft' | 'card' | 'menu' | 'play' | 'boards' | 'settings' | 'telemetry' | 'recording' | 'pause';

const BEST_KEY = GAUNTLET_BEST_KEY;

export class App {
  private settings: Settings;
  private readonly telemetry: Telemetry;
  private readonly audio = new AudioCues();
  private readonly haptics = new Haptics();
  private readonly renderer: PixiRenderer;
  private readonly input: InputController;
  private layout: Layout;
  private screen: Screen = 'menu';

  private state: SimState | null = null;
  private config: SimConfig | null = null;
  private recorder: Recorder | null = null;
  private replaying: Recording | null = null;
  private replayIdx = 0;
  private lastRecording: Recording | null = null;
  private runEnded = false;
  private endedAt = 0;
  private banner = '';
  private subBanner = '';
  /** A4: Grandpa's one-shot hint line and the meter throb that goes with it (ms timestamps). */
  private grandpaLine: { text: string; until: number } | null = null;
  private meterThrobUntil = 0;
  private flashTicks = 0;
  private acc = 0;
  private lastT = 0;
  private viewerIndex = 0;
  /** Restart latency measurement: set at death / time-up tap, cleared on the first playable tick. */
  private pendingRestartAt: number | null = null;
  private pendingRestartKind: 'death' | 'timeUp' = 'death';
  /** When the skip-eligible input arrived during the death pause (null = auto respawn). */
  private restartInputAt: number | null = null;
  private restartVia: 'pointerdown' | 'key' | 'auto' = 'auto';
  private viewerState: SimState | null = null;
  private readonly idleState: SimState = createSim({ mode: 'campaign', seed: 1, boards: [BOARDS[0]] });
  private readonly pauseBtn: HTMLButtonElement;
  private readonly overlay: HTMLDivElement;

  private readonly artReport: ArtLoadReport | null;
  private readonly title: TitleScreen;
  private readonly shaft: ShaftScreen;
  private readonly card: LevelCardScreen;
  private cardIndex = 0;
  private progress: Progress = loadProgress();
  /** Per-level counters for stars. */
  private levelDeaths = 0;
  private levelOctaves = 0;
  private levelCrushKills = 0;

  constructor(canvas: HTMLCanvasElement, overlay: HTMLDivElement, renderer: PixiRenderer, artReport: ArtLoadReport | null = null) {
    this.overlay = overlay;
    this.renderer = renderer;
    this.artReport = artReport;
    this.settings = loadSettings();
    this.telemetry = new Telemetry(this.settings);
    if (artReport) this.telemetry.artLoad(artReport, BUILD_STAMP);
    this.layout = computeLayout(window.innerWidth, window.innerHeight, DEFAULTS.fieldWidth, DEFAULTS.fieldHeight, this.settings);
    this.audio.enabled = this.settings.sound;
    this.haptics.enabled = this.settings.haptics;
    this.renderer.setArtSource(this.settings.artSource);
    this.input = new InputController(canvas, {
      getLayout: () => this.layout,
      getFireVariant: () => this.settings.fireVariant,
      getPlayerScreenPos: () => this.playerScreenPos(),
      isDpadEnabled: () => this.settings.dpad,
      onAccidentalFire: (reason) => {
        if (this.screen === 'play' && !this.replaying) this.telemetry.accidentalFire(reason);
      },
      onSwipe: () => this.audio.unlock(),
      onTapStop: () => {},
      onFirePress: () => this.audio.unlock(),
      onAnyTap: () => this.onAnyTap(),
      isBeamReady: () => !!this.state && this.screen === 'play' && this.state.lantern.charges >= DEFAULTS.lanternChargesPerBeam,
      onBeamPress: () => this.audio.unlock(),
    });
    this.card = new LevelCardScreen(() => this.renderer.atlas, {
      onDig: (picks) => {
        const used = consumeKit(this.progress, picks);
        this.startRun('campaign', undefined, this.cardIndex, used);
      },
      onBack: () => this.showShaft(),
    });
    this.title = new TitleScreen(() => this.renderer.atlas, {
      onDig: () => this.showShaft(),
      onGauntlet: () => this.startRun('gauntlet'),
      onSettings: () => this.showSettings(),
      onDevMenu: () => this.showMenu(),
      nextLevelLabel: () => {
        const i = nextLevelIndex(this.progress, BOARDS.map((b) => b.id));
        return `${SHAFT_LABEL} · Level ${i + 1}`;
      },
      gauntletBest: () => this.bestScore(),
    });
    this.shaft = new ShaftScreen(() => this.renderer.atlas, {
      onPlay: (i) => this.showCard(i),
      onBack: () => this.showTitle(),
      levels: () => this.shaftLevels(),
    });
    this.pauseBtn = document.getElementById('pause') as HTMLButtonElement;
    this.pauseBtn.addEventListener('click', () => this.pause());
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.screen === 'play') this.pause();
    });
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('orientationchange', () => setTimeout(() => this.resize(), 100));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.screen === 'play' && !this.runEnded) this.pause();
    });
  }

  start(): void {
    this.resize();
    this.showTitle();
    this.lastT = performance.now();
    requestAnimationFrame((t) => this.frame(t));
  }

  // ---------------------------------------------------------------------
  // Loop

  private frame(t: number): void {
    const dt = Math.min(250, t - this.lastT);
    this.lastT = t;
    this.renderer.update(dt);
    if (this.screen === 'title' || this.screen === 'shaft' || this.screen === 'card') {
      (this.screen === 'title' ? this.title : this.screen === 'shaft' ? this.shaft : this.card).update(dt);
      this.renderer.drawScreen();
      requestAnimationFrame((tt) => this.frame(tt));
      return;
    }
    if (this.screen === 'play' && this.state && this.config) {
      // Hit-stop: freeze the sim for a few ms on big impacts; keep rendering.
      if (!this.renderer.camera.consumeHitStop(dt)) this.acc += dt;
      let steps = 0;
      while (this.acc >= DEFAULTS.tickMs && steps < 6) {
        this.tick();
        this.acc -= DEFAULTS.tickMs;
        steps++;
      }
      if (steps === 6) this.acc = 0; // fell far behind: drop time rather than spiral
      this.renderer.draw(this.state, this.acc / DEFAULTS.tickMs, this.layout, this.settings, this.hud());
    } else if (this.screen === 'boards' && this.viewerState) {
      this.renderer.draw(this.viewerState, 0, this.layout, this.settings, { mode: 'viewer', fireFrac: 0, fireVariant: this.settings.fireVariant, idleMs: 0, levelName: '' });
    } else if (this.state && (this.screen === 'pause' || this.screen === 'menu')) {
      this.renderer.draw(this.state, 0, this.layout, this.settings, this.hud());
    } else {
      this.renderer.draw(this.idleState, 0, this.layout, this.settings, { mode: 'viewer', fireFrac: 0, fireVariant: this.settings.fireVariant, idleMs: 0, levelName: '' });
    }
    requestAnimationFrame((tt) => this.frame(tt));
  }

  private tick(): void {
    const state = this.state!;
    const config = this.config!;
    let input: InputFrame;
    if (this.replaying) {
      this.input.consumeFrame();
      input = NO_INPUT;
      const rec = this.replaying;
      const nextTick = state.tick + 1;
      if (this.replayIdx < rec.inputs.length && rec.inputs[this.replayIdx][0] === nextTick) {
        input = unpackInput(rec.inputs[this.replayIdx][1]);
        this.replayIdx++;
      }
      if (nextTick > rec.ticks) {
        this.finishReplay();
        return;
      }
    } else {
      input = this.runEnded ? NO_INPUT : this.input.consumeFrame();
      this.recorder?.record(state.tick + 1, input);
      if (state.phase === 'dying' && this.restartInputAt === null && (input.skip || input.dir || input.stop || input.fire)) {
        const elapsed = DEFAULTS.deathTicks - state.phaseTimer + 1;
        if (elapsed >= DEFAULTS.deathSkipAfterTicks) {
          this.restartInputAt = this.input.lastInputAt; // the physical event time, not the tick that consumed it
          this.restartVia = input.skip ? 'pointerdown' : 'key';
        }
      }
    }
    const events = step(state, input, config);
    if (this.flashTicks > 0) this.flashTicks--;
    this.telemetry.setTick(state.tick);
    for (const ev of events) this.onEvent(ev, state);
    if (this.pendingRestartAt !== null && state.phase === 'playing') {
      const now = performance.now();
      this.telemetry.restartLatency({
        kind: this.pendingRestartKind,
        ms: now - this.pendingRestartAt,
        inputMs: this.restartInputAt !== null ? now - this.restartInputAt : this.pendingRestartKind === 'timeUp' ? now - this.pendingRestartAt : null,
        via: this.pendingRestartKind === 'timeUp' ? 'pointerdown' : this.restartVia,
      });
      this.pendingRestartAt = null;
      this.restartInputAt = null;
      this.restartVia = 'auto';
    }
    // Wobble creak: pitch rises with the wobble progress (every third tick).
    for (const sk of state.sacks) {
      if (sk.state === 'wobble' && sk.wobble % 3 === 1) this.audio.play('creak', sk.wobble / DEFAULTS.sackWobbleTicks);
    }
  }

  private onEvent(ev: SimEvent, state: SimState): void {
    if (!this.replaying) this.telemetry.onSimEvent(ev, state.boardId, state.level, state.tick);
    this.renderer.onEvent(ev, state);
    switch (ev.type) {
      case 'wobbleStart':
        this.audio.play('wobble');
        this.haptics.vibrate(30);
        break;
      case 'sackFall':
        this.haptics.vibrate(20);
        break;
      case 'flameReady':
        this.audio.play('ping');
        break;
      case 'lanternFill':
        // A4: the first full lantern on this profile gets a meter throb and Grandpa's hint, once.
        if (ev.full && !this.replaying && state.phase === 'playing' && markSeen(this.progress, 'fullLantern')) {
          const now = performance.now();
          this.grandpaLine = { text: HINTS.fullLantern, until: now + 3200 };
          this.meterThrobUntil = now + 2600;
        }
        break;
      case 'gem':
        this.audio.play('gem', Math.min(1, ev.chain / DEFAULTS.octaveLength));
        break;
      case 'dig':
        if (state.tick % 2 === 0) this.audio.play('step');
        break;
      case 'mutate':
        this.audio.play(ev.kind === 'borer' ? 'mutate' : 'revert');
        this.haptics.vibrate(15);
        break;
      case 'bonusEnd':
        this.audio.play('bonusEnd');
        break;
      case 'sackLand':
        this.audio.play('thud', ev.rows / 3);
        break;
      case 'sackBreak':
        this.audio.play('coins');
        break;
      case 'creatureKill':
        if (ev.by === 'sack') {
          this.haptics.vibrate([30, 30, 60]);
          this.levelCrushKills++;
        }
        break;
      case 'playerDeath':
        this.levelDeaths++;
        this.audio.play('bonk');
        this.haptics.vibrate(60);
        this.pendingRestartAt = performance.now();
        this.pendingRestartKind = 'death';
        this.restartInputAt = null;
        this.restartVia = 'auto';
        break;
      case 'fire':
        this.audio.play('fire');
        break;
      case 'fireBlocked':
        if (ev.reason === 'dry') this.audio.play('click');
        break;
      case 'beam':
        this.audio.play('beam');
        this.haptics.vibrate([20, 30, 60]);
        this.flashTicks = 4;
        break;
      case 'kitApplied':
        this.audio.play(ev.kind === 'fruit' ? 'bonus' : 'ping');
        this.haptics.vibrate(25);
        break;
      case 'shieldPop':
        if (ev.cause !== 'timeout') {
          this.audio.play('pop');
          this.haptics.vibrate(40);
        }
        break;
      case 'bonusStart':
        this.audio.play('bonus');
        this.flashTicks = 8;
        break;
      case 'creatureSpawn':
        if (ev.last) this.haptics.vibrate([40, 60, 40]);
        break;
      case 'levelClear':
        this.banner = 'LEVEL CLEAR';
        this.subBanner = '';
        if (!this.replaying && this.config?.mode === 'campaign') {
          const lp = recordClear(this.progress, state.boardId, { deaths: this.levelDeaths, octaves: this.levelOctaves, crushKills: this.levelCrushKills, totalCreatures: state.total, score: state.score });
          this.subBanner = `${'★'.repeat(lp.stars)}${'☆'.repeat(3 - lp.stars)}`;
        }
        break;
      case 'levelStart':
        this.banner = '';
        this.resetLevelCounters();
        if (!this.replaying && this.config?.mode === 'campaign') recordAttempt(this.progress, ev.boardId);
        break;
      case 'octave':
        this.levelOctaves++;
        break;
      case 'gameOver':
        this.endRun('gameOver', 'GAME OVER', 'tap for the shaft');
        break;
      case 'won':
        this.endRun('won', 'SHAFT CLEARED', `score ${state.score} · tap for the shaft`);
        break;
      case 'timeUp':
        this.endRun('timeUp', 'TIME', `score ${state.score} · tap to restart`);
        break;
      default:
        break;
    }
  }

  private readInsets(): Insets {
    // env(safe-area-inset-*) via a probe element; zero where unsupported.
    let probe = document.getElementById('safe-probe');
    if (!probe) {
      probe = document.createElement('div');
      probe.id = 'safe-probe';
      probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
      document.body.appendChild(probe);
    }
    const cs = getComputedStyle(probe);
    const n = (v: string) => Math.max(0, parseFloat(v) || 0);
    return { top: n(cs.paddingTop), right: n(cs.paddingRight), bottom: n(cs.paddingBottom), left: n(cs.paddingLeft) };
  }

  private hud(): HudInfo {
    const s = this.state!;
    const c = this.config!;
    return {
      mode: this.replaying ? 'replay' : c.mode,
      fireVariant: this.settings.fireVariant,
      idleMs: this.runEnded ? 0 : performance.now() - this.input.lastInputAt,
      levelName: BOARDS_BY_ID.get(s.boardId)?.name ?? s.boardId,
      closedSack: this.settings.closedSack,
      timeLeftMs: c.timeLimitTicks !== undefined ? (c.timeLimitTicks - s.tick) * DEFAULTS.tickMs : undefined,
      banner: this.banner || undefined,
      subBanner: this.subBanner,
      fireFrac: 1 - Math.min(1, s.lantern.fill / DEFAULTS.lanternGemsPerShot),
      lantern: { fill: s.lantern.fill, gemsPerShot: DEFAULTS.lanternGemsPerShot, charges: s.lantern.charges, maxCharges: DEFAULTS.lanternMaxCharges, beamReady: s.lantern.charges >= DEFAULTS.lanternChargesPerBeam, chargeFill: s.lantern.chargeFill, gemsPerCharge: s.params.gemsPerCharge },
      dpadHeld: this.input.dpadHeld,
      flashTicks: this.flashTicks,
      grandpaLine: this.grandpaLine && performance.now() < this.grandpaLine.until ? this.grandpaLine.text : undefined,
      meterThrob: performance.now() < this.meterThrobUntil,
    };
  }

  private playerScreenPos(): [number, number] | null {
    if (this.screen !== 'play' || !this.state) return null;
    const [fx, fy] = moverPos(this.state.player, this.acc / DEFAULTS.tickMs);
    const L = this.layout;
    return [L.fieldX + (fx + 0.5) * L.cell, L.fieldY + (fy + 0.5) * L.cell];
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.layout = computeLayout(w, h, DEFAULTS.fieldWidth, DEFAULTS.fieldHeight, this.settings, this.readInsets());
    this.renderer.resize(w, h);
    this.title.layout(this.layout);
    this.shaft.layout(this.layout);
    this.card.layout(this.layout);
    // Pause button sits in the floor strip, opposite the lantern, clear of the safe area.
    const L = this.layout;
    const onLeft = this.settings.fireVariant === 'lantern' && this.settings.fireSide === 'right';
    this.pauseBtn.style.top = `${Math.round(L.floorY + 10)}px`;
    this.pauseBtn.style.left = onLeft ? `${L.insets.left + 10}px` : 'auto';
    this.pauseBtn.style.right = onLeft ? 'auto' : `${L.insets.right + 10}px`;
  }

  // ---------------------------------------------------------------------
  // Runs

  private newSeed(): number {
    return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
  }

  private startRun(mode: 'campaign' | 'gauntlet', seed = this.newSeed(), startIndex = 0, picks: Pick[] = []): void {
    const speed = PLAYER_SPEEDS[this.settings.playerSpeed];
    this.config = {
      mode,
      seed,
      boards: mode === 'campaign' ? BOARDS.slice(startIndex) : GAUNTLET_BOARDS,
      timeLimitTicks: mode === 'gauntlet' ? GAUNTLET_TICKS : undefined,
      infiniteLives: mode === 'gauntlet',
      playerTicksPerCellH: speed.h,
      playerTicksPerCellV: speed.v,
      picks: picks.length ? picks : undefined,
    };
    this.beginState(createSim(this.config));
    this.recorder = new Recorder(this.config);
    this.replaying = null;
    this.telemetry.levelStart(this.state!.boardId, this.state!.level);
    this.resetLevelCounters();
    if (mode === 'campaign') recordAttempt(this.progress, this.state!.boardId);
  }

  private resetLevelCounters(): void {
    this.levelDeaths = 0;
    this.levelOctaves = 0;
    this.levelCrushKills = 0;
  }

  private shaftLevels(): ShaftLevel[] {
    const ids = BOARDS.map((b) => b.id);
    const next = nextLevelIndex(this.progress, ids);
    return BOARDS.map((b, i) => {
      const lp = levelProgress(this.progress, b.id);
      return { id: b.id, name: b.name, index: i, hard: b.hard || b.superHard, cleared: lp.cleared, stars: lp.stars, next: i === next, locked: i > next };
    });
  }

  private showTitle(): void {
    this.screen = 'title';
    this.pauseBtn.hidden = true;
    this.overlay.hidden = true;
    this.title.refreshTextures();
    this.title.layout(this.layout);
    this.renderer.showScreen(this.title.root);
  }

  /** Level card: name, Target preview and the pre-level picker (Systems Brief §2.2). */
  private showCard(index: number): void {
    const b = BOARDS[index];
    if (!b) return;
    this.cardIndex = index;
    this.screen = 'card';
    this.pauseBtn.hidden = true;
    this.overlay.hidden = true;
    this.card.show({ index, name: b.name, hard: b.hard, superHard: b.superHard, goals: b.objectives.goals.map((g) => ({ type: g.type, gemType: g.gemType, target: g.target, phase: g.phase })), stock: { ...kitStock(this.progress) } });
    this.card.layout(this.layout);
    this.renderer.showScreen(this.card.root);
  }

  private showShaft(): void {
    this.screen = 'shaft';
    this.pauseBtn.hidden = true;
    this.overlay.hidden = true;
    this.shaft.refreshTextures();
    this.shaft.layout(this.layout);
    this.renderer.showScreen(this.shaft.root);
  }

  private beginState(state: SimState): void {
    this.renderer.showScreen(null);
    this.state = state;
    this.runEnded = false;
    this.banner = '';
    this.subBanner = '';
    this.flashTicks = 0;
    this.acc = 0;
    this.input.consumeFrame();
    this.screen = 'play';
    this.overlay.hidden = true;
    this.overlay.classList.remove('bottom');
    this.pauseBtn.hidden = false;
    this.audio.unlock();
  }

  private endRun(outcome: string, banner: string, sub: string): void {
    if (this.runEnded) return;
    this.runEnded = true;
    this.pendingRestartAt = null;
    this.endedAt = performance.now();
    this.banner = banner;
    this.subBanner = sub;
    const s = this.state!;
    const c = this.config!;
    if (this.replaying) return;
    this.telemetry.levelAbandon(outcome);
    this.lastRecording = this.recorder?.finish(s) ?? null;
    this.telemetry.runEnd({ mode: c.mode, seed: c.seed, score: s.score, ticks: s.tick, outcome, levelsCleared: s.levelsCleared });
    if (c.mode === 'gauntlet') {
      const best = this.bestScore();
      if (s.score > best) localStorage.setItem(BEST_KEY, String(s.score));
    }
  }

  private bestScore(): number {
    return Number(localStorage.getItem(BEST_KEY) ?? 0);
  }

  private onAnyTap(): void {
    if (this.screen !== 'play' || !this.state || !this.runEnded) return;
    if (performance.now() - this.endedAt < 400) return;
    if (this.replaying) {
      this.replaying = null;
      this.showMenu();
      return;
    }
    if (this.state.phase === 'timeUp') {
      this.pendingRestartAt = this.input.lastInputAt;
      this.pendingRestartKind = 'timeUp';
      this.startRun('gauntlet'); // one-tap restart
      return;
    }
    if (this.config?.mode === 'campaign') this.showShaft();
    else this.showTitle();
  }

  private pause(): void {
    if (this.screen !== 'play') return;
    this.screen = 'pause';
    this.pauseBtn.hidden = true;
    this.showPanel(`
      <h1>Paused</h1>
      <button class="primary" data-a="resume">Resume</button>
      <button data-a="quit">Quit to menu</button>
    `);
  }

  private quitRun(): void {
    if (this.state && !this.runEnded && !this.replaying) {
      this.telemetry.levelAbandon('quit');
      this.lastRecording = this.recorder?.finish(this.state) ?? null;
      this.telemetry.runEnd({ mode: this.config!.mode, seed: this.config!.seed, score: this.state.score, ticks: this.state.tick, outcome: 'quit', levelsCleared: this.state.levelsCleared });
      this.runEnded = true;
    }
    this.replaying = null;
    this.showTitle();
  }

  // ---------------------------------------------------------------------
  // Replay

  private startVisualReplay(rec: Recording): void {
    const config = configFromRecording(rec, BOARDS_BY_ID);
    this.config = config;
    this.replaying = rec;
    this.replayIdx = 0;
    this.recorder = null;
    this.beginState(createSim(config));
    this.banner = '';
  }

  private finishReplay(): void {
    const rec = this.replaying!;
    const hash = hashState(this.state!);
    const ok = hash === rec.finalHash;
    this.runEnded = true;
    this.endedAt = performance.now();
    this.banner = ok ? 'REPLAY OK' : 'REPLAY MISMATCH';
    this.subBanner = `${hash} ${ok ? '=' : '≠'} ${rec.finalHash} · tap for menu`;
  }

  // ---------------------------------------------------------------------
  // Screens

  private showPanel(html: string, bottom = false): void {
    if (this.screen !== 'play' && this.screen !== 'pause' && this.screen !== 'boards') this.renderer.showScreen(null);
    this.overlay.innerHTML = `<div class="panel">${html}</div>`;
    this.overlay.hidden = false;
    this.overlay.classList.toggle('bottom', bottom);
    this.overlay.querySelectorAll<HTMLElement>('[data-a]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.action(el.dataset.a!, el);
      });
    });
  }

  /** Dev menu (behind a 2 s long-press on the title wordmark). */
  private showMenu(): void {
    this.screen = 'menu';
    this.renderer.showScreen(null);
    this.pauseBtn.hidden = true;
    const best = this.bestScore();
    const last = this.lastRecording;
    this.showPanel(`
      <h1>${GAME_NAME} · dev</h1>
      <button class="primary" data-a="title">Back to title</button>
      <p class="small">Tester build: <a href="${TESTER_URL}" style="color:#ffb27a">${TESTER_URL}</a></p>
      <p class="small">v${GAME_VERSION} · build ${BUILD_STAMP} · art ${this.artReport ? `${this.artReport.loaded}/${this.artReport.requested} (${this.artReport.source}, ${this.artReport.ms} ms)` : 'n/a'}</p>
      ${this.artReport && this.artReport.loaded < this.artReport.requested ? `<p class="small" style="color:#ff9a7a">Real art failed for ${this.artReport.requested - this.artReport.loaded} file(s); placeholders are shown for those. ${this.artReport.firstError ? `First error: ${escapeHtml(this.artReport.firstError)}` : ''}</p>` : ''}
      ${this.artReport && this.artReport.requested === 0 ? `<p class="small" style="color:#ff9a7a">No art bundle found; showing placeholders.</p>` : ''}
      <button class="primary" data-a="campaign">Campaign — 3 boards</button>
      <button data-a="gauntlet">Gauntlet — 90 s, infinite lives${best ? ` · best ${best}` : ''}</button>
      <button data-a="boards">Board viewer</button>
      <button data-a="settings">Settings</button>
      <button data-a="telemetry">Telemetry</button>
      <button data-a="recording">Recording / replay${last ? ` · last: ${last.mode} ${last.finalScore} pts` : ''}</button>
      <p class="small">Swipe anywhere on the screen to steer (the turn happens at the next cell boundary and waits until it can). Tap the miner to stop. Fire: ${this.settings.fireVariant === 'lantern' ? 'tap the lantern in the floor' : 'double-tap anywhere'} (switch in Settings). Keyboard: arrows/WASD, space = fire, X = stop.</p>
    `);
  }

  private showSettings(): void {
    this.screen = 'settings';
    const s = this.settings;
    const chk = (k: keyof Settings) => (s[k] ? 'checked' : '');
    this.showPanel(`
      <h1>Settings</h1>
      <p class="small">Fire A/B (Bible §3). Lantern: a brass lantern in the floor scenery, thumb-reachable. Double-tap: double-tap anywhere fires in the facing direction; a flame icon beside the miner shows recharge.</p>
      <div class="row"><span>Fire</span>
        <button data-a="variant-lantern" ${s.fireVariant === 'lantern' ? 'class="primary"' : ''}>A · Lantern</button>
        <button data-a="variant-doubleTap" ${s.fireVariant === 'doubleTap' ? 'class="primary"' : ''}>B · Double-tap</button></div>
      <div class="row"><span>Lantern side</span>
        <button data-a="fire-left" ${s.fireSide === 'left' ? 'class="primary"' : ''}>Left</button>
        <button data-a="fire-right" ${s.fireSide === 'right' ? 'class="primary"' : ''}>Right</button></div>
      <label class="toggle">Legacy virtual d-pad (off by default) <input type="checkbox" data-a="toggle-dpad" ${chk('dpad')}></label>
      <label class="toggle">Sound <input type="checkbox" data-a="toggle-sound" ${chk('sound')}></label>
      <label class="toggle">Haptics <input type="checkbox" data-a="toggle-haptics" ${chk('haptics')}></label>
      <label class="toggle">Gem discovery (half-buried until seen) <input type="checkbox" data-a="toggle-gem-discovery" ${chk('gemDiscovery')}></label>
      <label class="toggle">Player speed
        <select data-a="speed">
          ${(Object.keys(PLAYER_SPEEDS) as (keyof typeof PLAYER_SPEEDS)[]).map((k) => `<option value="${k}" ${s.playerSpeed === k ? 'selected' : ''}>${PLAYER_SPEEDS[k].label}</option>`).join('')}
        </select></label>
      <h2>Save data</h2>
      <p class="small">Progress, settings and the Gauntlet best live in this browser. Export to keep them or move them; import merges (never loses a star). Testers: use "Share telemetry" to send your sessions back.</p>
      <div class="row"><button data-a="save-export">Export save</button><button data-a="save-copy">Copy save</button></div>
      <label class="toggle">Import save file <input type="file" accept="application/json" data-a="save-import-file"></label>
      <textarea data-a="save-paste" rows="3" placeholder="…or paste a save here" style="width:100%;font:12px monospace;background:#15121c;color:#f0ead6;border:0;border-radius:10px;padding:8px;box-sizing:border-box"></textarea>
      <div class="row"><button data-a="save-import-paste">Import pasted (merge)</button><button data-a="save-import-replace">Import pasted (replace)</button></div>
      <div class="row"><button data-a="telemetry-share">Share telemetry</button><button data-a="dl-telemetry">Download telemetry</button><button data-a="copy-telemetry">Copy telemetry</button></div>
      <p class="small" data-a="save-status"></p>
      <h2>Dev</h2>
      <label class="toggle">Real art (off = placeholders) <input type="checkbox" data-a="toggle-art" ${s.artSource === 'real' ? 'checked' : ''}></label>
      <label class="toggle">Closed sack A/B <input type="checkbox" data-a="toggle-closed-sack" ${chk('closedSack')}></label>
      <button class="primary" data-a="title">Back to title</button>
      <button data-a="menu">Dev menu</button>
    `);
    this.overlay.querySelectorAll<HTMLInputElement>('input[data-a],select[data-a]').forEach((el) => {
      el.addEventListener('change', () => this.action(el.dataset.a!, el));
    });
  }

  private saveStatus(msg: string): void {
    const el = this.overlay.querySelector<HTMLElement>('[data-a="save-status"]');
    if (el) el.textContent = msg;
  }

  private applyImport(text: string, mode: 'merge' | 'replace'): void {
    const r = importSave(localStorage, text, mode);
    if (!r.ok) {
      this.saveStatus(`Import failed: ${r.error}`);
      return;
    }
    // Reload the in-memory copies so the title, shaft and settings reflect the file.
    this.progress = loadProgress();
    this.settings = loadSettings();
    this.renderer.setArtSource(this.settings.artSource);
    this.saveStatus(`Imported (${r.summary!.mode}): ${r.summary!.levels} level(s), Gauntlet best ${r.summary!.gauntletBest}${r.summary!.sessions ? `, ${r.summary!.sessions} telemetry session(s)` : ''}.`);
  }

  /** Web Share with a file where the browser allows it (mobile), else the clipboard. */
  private async shareText(name: string, text: string, title: string): Promise<string> {
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    try {
      const file = new File([text], name, { type: 'application/json' });
      if (nav.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title });
        return 'Shared.';
      }
      if (nav.share) {
        await nav.share({ title, text });
        return 'Shared.';
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') return 'Share cancelled.';
    }
    await navigator.clipboard?.writeText(text);
    return 'Copied to clipboard (sharing not available here).';
  }

  private showTelemetry(): void {
    this.screen = 'telemetry';
    const stored = loadStoredSessions().length;
    this.showPanel(`
      <h1>Telemetry</h1>
      <pre>${escapeHtml(this.telemetry.summary())}</pre>
      <p class="small">${stored} session(s) stored locally. Export includes attemptsPerLevel, timeToPass, timeToAbandon, missedTurnEvents, accidentalFireEvents and sessionLength for every stored session.</p>
      <button class="primary" data-a="dl-telemetry">Download telemetry JSON</button>
      <button data-a="copy-telemetry">Copy telemetry JSON to clipboard</button>
      <button data-a="clear-telemetry">Clear stored sessions</button>
      <button data-a="menu">Back</button>
    `);
  }

  private showRecording(msg = ''): void {
    this.screen = 'recording';
    const r = this.lastRecording;
    this.showPanel(`
      <h1>Recording</h1>
      ${r ? `<p>Last run: ${r.mode}, seed ${r.seed}, ${r.ticks} ticks, ${r.inputs.length} inputs, score ${r.finalScore}, hash ${r.finalHash}</p>` : '<p>No recording yet. Play a run first.</p>'}
      ${msg ? `<pre>${escapeHtml(msg)}</pre>` : ''}
      <button class="primary" data-a="verify" ${r ? '' : 'disabled'}>Verify headless (replay → compare hash)</button>
      <button data-a="replay" ${r ? '' : 'disabled'}>Watch replay</button>
      <button data-a="dl-recording" ${r ? '' : 'disabled'}>Download recording JSON</button>
      <label class="toggle">Load recording <input type="file" accept="application/json" data-a="load-recording"></label>
      <button data-a="menu">Back</button>
    `);
    const file = this.overlay.querySelector<HTMLInputElement>('input[type=file]');
    file?.addEventListener('change', async () => {
      const f = file.files?.[0];
      if (!f) return;
      try {
        this.lastRecording = JSON.parse(await f.text()) as Recording;
        this.showRecording('Loaded.');
      } catch (e) {
        this.showRecording(`Could not load: ${String(e)}`);
      }
    });
  }

  private showBoards(): void {
    this.screen = 'boards';
    this.pauseBtn.hidden = true;
    const b = ALL_BOARDS[this.viewerIndex];
    this.viewerState = createSim({ mode: 'campaign', seed: 1, boards: [b] });
    const rows = Object.entries(b.params)
      .map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`)
      .join('');
    this.showPanel(
      `
      <div class="row"><button data-a="prev">‹</button><h2 style="flex:2;text-align:center;margin:0">${this.viewerIndex + 1}/${ALL_BOARDS.length} · ${escapeHtml(b.name)}</h2><button data-a="next">›</button></div>
      <p class="small">${escapeHtml(b.teaches)}</p>
      <details><summary class="small">Ten difficulty params (${escapeHtml(b.id)})</summary><table>${rows}</table></details>
      <div class="row"><button class="primary" data-a="play-board">Play this board</button><button data-a="menu">Back</button></div>
    `,
      true,
    );
  }

  private action(a: string, el: HTMLElement): void {
    switch (a) {
      case 'campaign':
        this.startRun('campaign');
        break;
      case 'gauntlet':
        this.startRun('gauntlet');
        break;
      case 'boards':
        this.showBoards();
        break;
      case 'prev':
        this.viewerIndex = (this.viewerIndex + ALL_BOARDS.length - 1) % ALL_BOARDS.length;
        this.showBoards();
        break;
      case 'next':
        this.viewerIndex = (this.viewerIndex + 1) % ALL_BOARDS.length;
        this.showBoards();
        break;
      case 'play-board': {
        const speed = PLAYER_SPEEDS[this.settings.playerSpeed];
        this.config = { mode: 'campaign', seed: this.newSeed(), boards: [ALL_BOARDS[this.viewerIndex]], playerTicksPerCellH: speed.h, playerTicksPerCellV: speed.v };
        this.beginState(createSim(this.config));
        this.recorder = new Recorder(this.config);
        this.replaying = null;
        this.telemetry.levelStart(this.state!.boardId, this.state!.level);
        break;
      }
      case 'settings':
        this.showSettings();
        break;
      case 'telemetry':
        this.showTelemetry();
        break;
      case 'recording':
        this.showRecording();
        break;
      case 'menu':
        this.showMenu();
        break;
      case 'title':
        this.showTitle();
        break;
      case 'resume':
        this.overlay.hidden = true;
        this.screen = 'play';
        this.pauseBtn.hidden = false;
        this.acc = 0;
        this.input.consumeFrame();
        break;
      case 'quit':
        this.quitRun();
        break;
      case 'fire-left':
      case 'fire-right':
        this.updateSettings({ fireSide: a === 'fire-left' ? 'left' : 'right' });
        this.showSettings();
        break;
      case 'toggle-dpad':
        this.updateSettings({ dpad: (el as HTMLInputElement).checked });
        break;
      case 'variant-lantern':
      case 'variant-doubleTap':
        this.updateSettings({ fireVariant: a === 'variant-lantern' ? 'lantern' : 'doubleTap' });
        this.showSettings();
        break;
      case 'toggle-art':
        this.updateSettings({ artSource: (el as HTMLInputElement).checked ? 'real' : 'placeholder' });
        this.renderer.setArtSource(this.settings.artSource);
        this.title.refreshTextures();
        this.shaft.refreshTextures();
        break;
      case 'toggle-closed-sack':
        this.updateSettings({ closedSack: (el as HTMLInputElement).checked });
        break;
      case 'toggle-gem-discovery':
        this.updateSettings({ gemDiscovery: (el as HTMLInputElement).checked });
        break;
      case 'toggle-sound':
        this.updateSettings({ sound: (el as HTMLInputElement).checked });
        break;
      case 'toggle-haptics':
        this.updateSettings({ haptics: (el as HTMLInputElement).checked });
        break;
      case 'speed':
        this.updateSettings({ playerSpeed: (el as HTMLSelectElement).value as Settings['playerSpeed'] });
        break;
      case 'dl-telemetry':
        download(`sackfall-telemetry-${Date.now()}.json`, this.telemetry.exportJson());
        break;
      case 'copy-telemetry':
        void navigator.clipboard?.writeText(this.telemetry.exportJson()).then(() => this.saveStatus('Telemetry copied.'));
        break;
      case 'telemetry-share':
        void this.shareText(`sackfall-telemetry-${Date.now()}.json`, this.telemetry.exportJson(), `${GAME_NAME} telemetry`).then((m) => this.saveStatus(m));
        break;
      case 'save-export':
        download(`sackfall-save-${Date.now()}.json`, exportSaveJson(localStorage, { telemetry: true }));
        this.saveStatus('Save downloaded (includes telemetry).');
        break;
      case 'save-copy':
        void navigator.clipboard?.writeText(exportSaveJson(localStorage)).then(() => this.saveStatus('Save copied to clipboard.'));
        break;
      case 'save-import-file': {
        const f = (el as HTMLInputElement).files?.[0];
        if (f) void f.text().then((t) => this.applyImport(t, 'merge'));
        break;
      }
      case 'save-import-paste':
      case 'save-import-replace': {
        const ta = this.overlay.querySelector<HTMLTextAreaElement>('textarea[data-a="save-paste"]');
        if (ta && ta.value.trim()) this.applyImport(ta.value, a === 'save-import-replace' ? 'replace' : 'merge');
        else this.saveStatus('Paste a save first.');
        break;
      }
      case 'clear-telemetry':
        clearStoredSessions();
        this.telemetry.flush();
        this.showTelemetry();
        break;
      case 'verify': {
        const r = this.lastRecording!;
        const t0 = performance.now();
        const res = replay(r, BOARDS_BY_ID);
        this.showRecording(`${res.matches ? 'OK' : 'MISMATCH'}: replay hash ${res.hash} vs recorded ${r.finalHash} (${(performance.now() - t0).toFixed(0)} ms, score ${res.state.score})`);
        break;
      }
      case 'replay':
        this.startVisualReplay(this.lastRecording!);
        break;
      case 'dl-recording':
        download(`recording-${this.lastRecording!.seed}.json`, JSON.stringify(this.lastRecording));
        break;
      default:
        break;
    }
  }

  private updateSettings(patch: Partial<Settings>): void {
    this.settings = { ...this.settings, ...patch };
    saveSettings(this.settings);
    this.audio.enabled = this.settings.sound;
    this.haptics.enabled = this.settings.haptics;
    this.telemetry.updateSettings(this.settings);
    this.resize();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

function download(name: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 1000);
}
